import { Injectable } from '@angular/core';

export type MaybeNumberArray = Array<number|undefined>;

@Injectable({
  providedIn: 'root'
})
export class PaginationService {

  /**
   * Creates an array used in a pagination component for page links. Array contains integers and undefined elements.
   * See below for examples.
   *
   * @param selectedPage The number to start the array from, zero based.
   * @param numberOfPages The total number of pages.
   * @param maxShownPageNumberLinks The maximum continuous pages in an array. The total number of
   * elements in an array can be higher, see below.
   * When maxShownPageNumberLinks is undefined, the service generated an array from 0 till numberOfPages-1
   *
   * @returns An array that contains the numbers from start up to end, zero based.
   *
   * @example
   * maxShownPageNumberLinks is undefined and 10 pages in total:
   * [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
   *
   * @example
   * maxShownPageNumberLinks is 5 and 38 pages in total:
   * when selectedPage is 0, generated array: [0, 1, 2, 3, 4, undefined, 37]. Undefined is shown as 3 dots ... in a pagination component:
   *      < 1 2 3 4 5 ... 38 >
   *        ^ <-- selected page is 0
   *
   *  when selectedPage is 1
   *      < 1 2 3 4 5 ... 38 >
   *          ^
   *      < 1 2 3 4 5 ... 38 >
   *            ^
   *      < 1 2 3 4 5 ... 38 >
   *              ^
   *      < 1 ... 4 5 6 ... 38 >
   *                ^
   *      < 1 ... 5 6 7 ... 38 >
   *                ^
   *
   *      < 1 … 33 34 35 ... 38 >
   *                ^
   *      < 1 … 34 35 36 37 38 >
   *                ^
   *      < 1 … 34 35 36 37 38 >
   *                   ^
   *      < 1 … 34 35 36 37 38 >
   *                      ^
   *      < 1 … 34 35 36 37 38 >
   *                         ^ <-- selected page is 37
   */
  generatePageRange(selectedPage: number, numberOfPages: number, maxShownPageNumberLinks: number|undefined): MaybeNumberArray {
    if (selectedPage >= numberOfPages) {
      throw new RangeError(`selected page [${selectedPage}] is more or equal number of pages [${numberOfPages}]`);
    }
    let startPage = 0;
    let endPage = numberOfPages;
    if (maxShownPageNumberLinks !== undefined && maxShownPageNumberLinks > 0) {
      const maxSize = maxShownPageNumberLinks % 2 === 0 ? maxShownPageNumberLinks + 1 : maxShownPageNumberLinks;
      const isMaxSized = maxSize < numberOfPages;

      if (isMaxSized) {
        startPage = selectedPage - Math.floor(maxSize / 2);
        endPage = selectedPage + Math.floor(maxSize / 2) + 1;

        if (startPage < 0) {
          startPage = 0;
          endPage = Math.min(startPage + maxSize, numberOfPages);
        } else if (endPage > numberOfPages) {
          startPage = Math.max(numberOfPages - maxSize, 0);
          endPage = numberOfPages;
        }
      }
    }
    return this.generateRange(startPage, endPage, numberOfPages);
  }

  private generateRange(start: number, end: number, numberOfPages: number): MaybeNumberArray {
    if (end - start + 3 > numberOfPages) {
      return this.fullRange(numberOfPages);
    }
    let pages = this.basicRange(start, end);
    pages = this.handleStart(pages, start);
    pages = this.handleMiddle(pages, start, end, numberOfPages);
    pages = this.handleEnd(pages, end, numberOfPages);
    return pages;
  }

  private fullRange(numberOfPages: number): Array<number> {
    return Array.from({ length: numberOfPages }, (_v: unknown, k: number): number => k);
  }

  private basicRange(start: number, end: number): Array<number> {
    return Array.from({ length: end - start }, (_v: unknown, k: number): number => k + start);
  }

  private handleStart(pages: MaybeNumberArray, start: number): MaybeNumberArray {
    if (start === 1) {
      pages.unshift(0);
      pages.pop();
    }
    return pages;
  }

  private handleMiddle(pages: MaybeNumberArray, start: number, end: number, numberOfPages: number): MaybeNumberArray {
    if (start > 1) {
      const deletedCount = end === numberOfPages ? 0 : 1;
      pages.splice(0, deletedCount, 0, undefined);
      if (end < numberOfPages - 1) {
        pages.pop();
      }
    }
    return pages;
  }

  private handleEnd(pages: MaybeNumberArray, end: number, numberOfPages: number): MaybeNumberArray {
    if (end < numberOfPages - 1) {
      pages.push(undefined);
    }
    if (end < numberOfPages) {
      pages.push(numberOfPages - 1);
    }
    return pages;
  }
}
